/**
 * The application shell: masthead, navigation and the About panel.
 */

import { useEffect, useRef, useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'

import { api } from '../api/client'
import { PRODUCT_NAME, PRODUCT_PROGRAMME, PRODUCT_SUBTITLE, PRODUCT_TITLE } from '../lib/brand'
import { useAppStore } from '../store/useAppStore'
import { PublicBanner, ServerWaking, SiteFooter } from './PublicChrome'
import { SiteRail } from './SiteRail'

/** The path through the product, in order; the reference pages follow it. */
export const pagePath = (siteId: string) => [
  { to: `/site/${siteId}`, label: 'Overview' },
  { to: '/overview', label: 'Sites' },
  { to: '/setup', label: 'Scenario' },
  { to: '/simulation', label: 'Run' },
  { to: '/results', label: 'Inundation' },
  { to: '/compare-models', label: 'Comparison' },
  { to: '/alerts', label: 'Alert' },
  { to: '/impact', label: 'Impact' },
  { to: '/particles', label: '3D View' },
  { to: '/data', label: 'Provenance' },
  { to: '/scenarios', label: 'Library' },
  { to: '/health', label: 'Diagnostics' },
]

type SiteListing = {
  run_site: string
  sites: { id: string; display_name: string; runnable?: boolean }[]
}

function About() {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !box.current?.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('keydown', close)
    document.addEventListener('mousedown', close)
    return () => {
      document.removeEventListener('keydown', close)
      document.removeEventListener('mousedown', close)
    }
  }, [open])

  return (
    <div ref={box} className="relative shrink-0 self-center">
      <button type="button" className="ctl" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        About
      </button>
      {open && (
        <div role="dialog" aria-label="About" className="overlay absolute right-0 top-full z-50 mt-2 w-[400px] p-4">
          <div className="section-label">About</div>
          <p className="mt-2 text-14 font-medium leading-snug text-slate-100">{PRODUCT_TITLE}</p>
          <p className="mt-1 text-14 text-slate-400">{PRODUCT_SUBTITLE}</p>
          <p className="mt-3 border-t border-slate-800 pt-3 text-14 text-slate-300">{PRODUCT_PROGRAMME}</p>
        </div>
      )}
    </div>
  )
}

export function Layout() {
  const activeSiteId = useAppStore((s) => s.activeSiteId)
  const setActiveSiteId = useAppStore((s) => s.setActiveSiteId)
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => api.raw<SiteListing>('/sites') })
  const activeSiteName = sites.data?.sites.find((s) => s.id === activeSiteId)?.display_name ?? activeSiteId
  const runnable = (sites.data?.sites ?? []).filter((s) => s.runnable ?? s.id === sites.data?.run_site)

  // The run list is polled here so every page sees a finished run appear.
  useQuery({ queryKey: ['runs'], queryFn: api.runs, refetchInterval: 5000 })
  const health = useQuery({ queryKey: ['health'], queryFn: api.health })

  return (
    <div className="flex h-full min-w-0 flex-col bg-slate-950 lg:min-w-[1280px]">
      <ServerWaking />
      <PublicBanner />
      <header className="shrink-0 border-b border-slate-800 bg-slate-900">
        <div className="flex h-14 items-center gap-6 px-3 md:px-5">
          <div className="min-w-0" title={PRODUCT_TITLE}>
            <div className="text-24 font-semibold leading-none tracking-[0.14em] text-slate-100">
              {PRODUCT_NAME}
            </div>
            <div className="mt-1 truncate text-12 leading-4 text-slate-400">{PRODUCT_SUBTITLE}</div>
          </div>

          <div className="ml-auto flex items-center gap-4">
            {health.isError && (
              <span className="flex items-center gap-1.5 text-14 text-rose-400">
                <AlertTriangle className="h-4 w-4" />
                Backend unreachable
              </span>
            )}
            {/* On a phone the left rail is hidden; the site switcher lives here. */}
            <select
              value={activeSiteId}
              onChange={(e) => setActiveSiteId(e.target.value)}
              className="ctl md:hidden"
              aria-label="Site"
            >
              {runnable.map((s) => (
                <option key={s.id} value={s.id}>{s.display_name}</option>
              ))}
            </select>
            <div className="hidden text-right md:block">
              <div className="section-label">Site</div>
              <div className="text-14 font-medium leading-5 text-slate-100">{activeSiteName}</div>
            </div>
          </div>
        </div>

        <div className="flex items-stretch gap-4 px-3 md:px-5">
          <nav className="-mb-px flex min-w-0 flex-1 overflow-x-auto" aria-label="Primary">
            {pagePath(activeSiteId).map((page) => (
              <NavLink
                key={page.to}
                to={page.to}
                className={({ isActive }) =>
                  [
                    'whitespace-nowrap border-b-2 px-3 py-2.5 text-14 leading-5 transition-colors duration-150',
                    isActive
                      ? 'border-sky-500 font-medium text-slate-100'
                      : 'border-transparent text-slate-400 hover:text-slate-100',
                  ].join(' ')
                }
              >
                {page.label}
              </NavLink>
            ))}
          </nav>
          <About />
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <SiteRail />
        <main className="min-h-0 min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
      <SiteFooter />
    </div>
  )
}
